import { JsonObject, JsonValue, getOwn, isJsonObject, setOwn } from "../json";
import { Path, unwrapNamed } from "../types/type-system";
import { ConfigIssue, OutputDecl, TriggerRefresh } from "./kinds";

/** Error code of a refresh whose entity no longer exists (deleted). */
export const ENTITY_GONE = "ENTITY_GONE";

export interface RefreshRequest {
  entity: string;
  id: string;
  fields: string[];
}

export function liveFields(refresh: TriggerRefresh | null): Set<string> {
  return new Set((refresh?.entities ?? []).flatMap((e) => e.fields));
}

/** Whether a context path reads trigger data a refresh can change; event facts never change. */
export function readsLiveTriggerData(
  refresh: TriggerRefresh | null,
  path: Path,
): boolean {
  const field = path[0]?.field === "trigger" ? path[1]?.field : undefined;
  return field !== undefined && liveFields(refresh).has(field);
}

/** The entities a refresh re-reads: those whose id is in the payload. */
export function refreshRequests(
  refresh: TriggerRefresh | null,
  payload: JsonValue | undefined,
): RefreshRequest[] {
  if (!refresh || !isJsonObject(payload)) {
    return [];
  }
  return refresh.entities.flatMap((e) => {
    const id = getOwn(payload, e.id);
    return typeof id === "string" && id.length > 0
      ? [{ entity: e.entity, id, fields: e.fields }]
      : [];
  });
}

/**
 * The payload after a refresh: live fields come from `fresh`; event facts,
 * and live fields `fresh` leaves out, keep their values.
 */
export function mergeRefreshed(
  refresh: TriggerRefresh | null,
  previous: JsonValue,
  fresh: JsonValue,
): JsonValue {
  if (!isJsonObject(previous) || !isJsonObject(fresh)) {
    return previous;
  }
  const merged: JsonObject = { ...previous };
  for (const field of liveFields(refresh)) {
    const value = getOwn(fresh, field);
    if (value !== undefined) {
      setOwn(merged, field, value);
    }
  }
  return merged;
}

/** A refresh declaration must name real payload fields and never re-read an id. */
export function refreshIssues(
  refresh: TriggerRefresh | null,
  payload: OutputDecl,
): ConfigIssue[] {
  if (!refresh) {
    return [];
  }
  const rootName = unwrapNamed(payload.root).name;
  const root = payload.types.find((t) => t.name === rootName);
  const fields = root?.kind === "object" ? root.fields : {};
  const issues: ConfigIssue[] = [];
  const owner = new Map<string, string>();
  const ids = new Set(refresh.entities.map((e) => e.id));
  for (const e of refresh.entities) {
    if (!Object.hasOwn(fields, e.id)) {
      issues.push({
        path: "refresh",
        message: `${e.entity}: the id field ${e.id} isn't in the payload`,
      });
    }
    if (e.fields.length === 0) {
      issues.push({ path: "refresh", message: `${e.entity}: no live fields` });
    }
    for (const field of e.fields) {
      if (!Object.hasOwn(fields, field)) {
        issues.push({
          path: "refresh",
          message: `${e.entity}: ${field} isn't in the payload`,
        });
      }
      if (ids.has(field)) {
        issues.push({
          path: "refresh",
          message: `${e.entity}: ${field} identifies an entity, so it's an event fact`,
        });
      }
      const other = owner.get(field);
      if (other !== undefined) {
        issues.push({
          path: "refresh",
          message: `${field} is supplied by both ${other} and ${e.entity}`,
        });
      }
      owner.set(field, e.entity);
    }
  }
  return issues;
}
